import Facade from '@/Facade'
import { IInitResponse, Orientation, ShowRegularWinningsData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import gsap from 'gsap'
import { UIAPI } from '@modules/src/managers/UIAPI'
import { UIEvent, UIGameEvent } from '@modules/src/enums/UIEvents'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { Utils } from '@modules/src/utils/Utils'
import { RestoreUtil } from '@/utils/RestoreUtil'
import { Model } from '@/model/Model'
import { Utilities } from '@base/utils/Utilities'
import { Platform } from '@base/utils/Platform'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { toMoney } from '@base/utils/Formatter'
import { getNetworkCodeFromSite, resolveOperatorConfig } from '@modules/src/operatorConfig'
import { OperatorFeatureConfig } from '@modules/src/operatorConfig/types'
import { canAffordBuyBonus } from '@/managers/ui/BuyBonusManager'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { getUncreditedFreeSpinsWinMajorUnits } from '@/states/freespins/FreeSpinsFinalWin'
import { getMaxWinBetMultiplier } from '@/states/round/MaxWinCap'
import * as PIXI from 'pixi.js'

/**
 * Server entitlement for the session (game.misc.buy_bonus). It overrides the site config, which
 * can still ship hideBuyBonus: false while the backend refuses the purchase. Absent or unparsable
 * values leave the decision to the operator config, so older responses keep working.
 */
const isBuyBonusDisabledByServer = (initData: IInitResponse): boolean => {
    const raw = initData?.game?.misc?.buy_bonus

    if (raw === undefined || raw === null || raw === '') {
        return false
    }

    const parsed = Number(raw)

    return Number.isFinite(parsed) && parsed === 0
}

export class UIManager {
    private static readonly GLOBAL_SKIP_DEDUPLICATION_MS = 250
    private static readonly LEGACY_IDLE_TEXT = 'Click SPIN to play'
    private static readonly LEGACY_SPIN_START_TEXT = 'Good Luck!'
    private static readonly LEGACY_WINNING_COMBO_PREFIX = 'Winning Combo'
    private _model: Model
    private _currentUIState: string
    private _isSpacebarActive: boolean
    private _isQuickSpinActive: boolean
    private _isGameLocked: boolean
    private _isMenuShown: boolean
    private _isDialogShown: boolean
    private _isBuyBonusPopupOpen: boolean = false
    private _isSkipKeyUp: boolean
    private _infoTextObject: { textCounter: number; spinCounter: number; text: string; shownIndexes: number[] }
    private _uiPanelRoundWinTotal: number
    private _winningComboTickerDelay: gsap.core.Tween | null = null
    private _operatorConfig: OperatorFeatureConfig = {}
    private _lastLoadingProgress: number = 0
    private _lastGlobalSkipPointerAt: number = 0
    private _isSpinStopSoundArmed: boolean = false
    /** Reels are physically moving in these states, so a press here is a stop rather than a skip. */
    private static readonly REELS_SPINNING_STATES: ReadonlySet<string> = new Set<string>([
        GameStatesEnum.START_SPIN_STATE,
        GameStatesEnum.GET_SPIN_RESPONSE_STATE,
        GameStatesEnum.STOP_SPIN_STATE,
    ])
    private static readonly WIN_PRESENTATION_STATES: ReadonlySet<string> = new Set<string>([
        GameStatesEnum.REGULAR_WINNINGS_STATE,
        GameStatesEnum.LINE_BY_LINE_STATE,
        GameStatesEnum.BIG_WIN_STATE,
    ])
    private static UI_INFO_TEXT_SIZE: number = 9.7
    private static readonly DEFAULT_VOLUME: number = 0.5
    private static readonly UNMUTE_VOLUME: number = 0.5

    public constructor(model: Model) {
        this.setGameState('not_ready')

        this._isGameLocked = false
        this._isSpacebarActive = true
        this._model = model
        this._isSkipKeyUp = false
        this._uiPanelRoundWinTotal = 0
        this._infoTextObject = {
            textCounter: 0,
            spinCounter: 0,
            text: '',
            shownIndexes: [],
        }

        GameSignals.init.connect(this.onInit)
        GameSignals.displaySplashScreenState.connect(this.onGameIsReady)
        GameSignals.completeSplashScreenState.connect(this.onCompleteSplashScreenState)
        GameSignals.gamePause.connect(this.stopAutoPlay)
        GameSignals.gamePause.connect(this.onGamePause)
        GameSignals.gameResume.connect(this.onGameResume)
        GameSignals.stopAutoplay.connect(this.stopAutoPlay)
        GameSignals.updateAutoplayValue.connect(this.updateAutoplayValue)
        GameSignals.loadingProgress.connect(this.onLoadingProgress)
        GameSignals.betHasChanged.connect(this.onBetHasChanged)
        GameSignals.startSpin.connect(this.onStartSpin)
        GameSignals.updateWinPanelValue.connect(this.onUpdateWinPanelValueForUi)
        ;(GameSignals as any).setWinPanelValue?.connect(this.onSetWinPanelValueForUi)
        ;(GameSignals as any).setWinPanelValueAtLeast?.connect(this.onSetWinPanelValueAtLeastForUi)
        GameSignals.betCooldownEnded.connect(this.onStopSpin)
        GameSignals.balanceHasChanged.connect(this.onBalanceUpdate)
        GameSignals.updateUIWinField.connect(this.onUpdateUIWinField)
        GameSignals.lockGamePanel.connect(this.onLockGamePanel)
        GameSignals.unLockGamePanel.connect(this.onUnlockGamePanel)
        GameSignals.bonusStartComplete.connect(this.onBonusStartComplete)
        GameSignals.restoreFreeSpins.connect(this.updateFreeSpinsInfo.bind(this, true))
        GameSignals.showFreeSpinsSummary.connect(this.onFreeSpinsSummary)
        GameSignals.showFreeSpinsSummaryComplete.connect(this.onFreeRoundSpin.bind(this, true))
        GameSignals.bonusStart.connect(this.onBonusStart)
        GameSignals.bonusStopComplete.connect(this.onBonusStopComplete)
        GameSignals.serverError.connect(this.onServerError)
        GameSignals.realityCheck.connect(this.onServerError)
        GameSignals.sessionTimeout.connect(this.onServerError)
        GameSignals.errorConnection.connect(this.onServerError)
        GameSignals.commonError.connect(this.onServerError)
        GameSignals.hardwareAcceleration.connect(() => {
            this.displayUI(true)
        })
        GameSignals.freeRoundsActive.connect(this.onFreeRoundSpin.bind(this, true))
        GameSignals.roundStart.connect(this.onFreeRoundSpin)
        GameSignals.mute.connect(this.mute)
        GameSignals.unMute.connect(this.unMute)
        GameSignals.toggleTurbo.connect(this.toggleTurboSpin)
        GameSignals.togglePaytayble.connect(this.onTogglePaytable)
        GameSignals.toggleGameHelp.connect(this.onToggleGameHelp)
        GameSignals.betPlus.connect(this.onBetPlusClicked)
        GameSignals.betMinus.connect(this.onBetMinusClicked)
        GameSignals.skipIntroToggled.connect(this.toggleSkipIntro)
        GameSignals.premiumSpinsToggled.connect(this.togglePremiumSpinsMode)
        GameSignals.roundStop.connect(this.onRoundStop)
        GameSignals.idle.connect(this.onIdleState)
        GameSignals.showUI.connect(this.displayUI)
        GameSignals.fullScreen.connect(this.fullScreen)
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GameSignals.openBuyBonusPopup.connect(this.onBuyBonusPopupOpen)
        GameSignals.hidePopup.connect(this.onBuyBonusPopupClose)
        GameSignals.buyBonusResponse.connect(this.onBuyBonusPopupClose)

        UIAPI.emit(UIGameEvent.balance, { value: this._model.balance })
        GameSignals.balanceUpdate.emit(this._model.balance)
    }

    private onGamePause = () => {
        this._isGameLocked = true
    }

    private onGameResume = () => {
        this._isGameLocked = false
    }

    private toggleSkipIntro = (state: boolean) => {
        RestoreUtil.setKeyData(RestoreUtil.SKIP_INTRO_KEY, `${state}`)
        UIAPI.emit(UIGameEvent.show_splash, { value: !state })
    }
    private fullScreen = () => {
        UIAPI.emit(UIEvent.set_fullscreen, { value: true })
    }
    private isSpacebarBlockedByOverlay = (): boolean => {
        const isFsCollectPresentationActive = Boolean((Facade as any).isFsCollectPresentationActive)
        const isBigWinPresentationActive = Boolean((Facade as any).isBigWinPresentationActive)
        // Buy Bonus must block Space for the whole time the popup is up (including confirm→close).
        if (this._isBuyBonusPopupOpen) {
            return true
        }

        // Big win / FS collect: Space should skip (same as click-anywhere), not be blocked.
        if (Facade.isPopupBlockingInput && !isFsCollectPresentationActive && !isBigWinPresentationActive) {
            return true
        }

        return this._isMenuShown || this._isDialogShown
    }

    private onSpaceKeyDown = (event: KeyboardEvent): void => {
        if (event.code !== 'Space' && event.key !== ' ') {
            return
        }

        if (!this._isSpacebarActive || this.isSpacebarBlockedByOverlay()) {
            event.preventDefault()
        }
    }

    private onSpaceKeyUp = (event: KeyboardEvent): void => {
        if (event.code !== 'Space' && event.key !== ' ') {
            return
        }

        if (!this._isSpacebarActive || this.isSpacebarBlockedByOverlay()) {
            event.preventDefault()
            return
        }

        if (this._isSkipKeyUp) {
            this._isSkipKeyUp = false
            return
        }

        switch (this._currentUIState) {
            case 'ready':
                UIAPI.emit(UIEvent.clicked_play)
                UIAPI.emit(UIEvent.set_fullscreen)
                break
            case 'playing':
                this.requestSpinStopSound()
                GameSignals.stopButtonClicked.emit()
                break
        }
    }

    private addKeyboardListener = () => {
        if (this._isGameLocked) return

        window.addEventListener('pointerdown', this.onGlobalSkipPointerDown, true)
        // Capture phase so Buy Bonus / overlays win over any other Space handlers.
        document.addEventListener('keydown', this.onSpaceKeyDown, true)
        document.addEventListener('keyup', this.onSpaceKeyUp, true)
    }

    private onGlobalSkipPointerDown = (event: PointerEvent): void => {
        const isFsCollectPresentationActive = Boolean((Facade as any).isFsCollectPresentationActive)
        const isBigWinPresentationActive = Boolean((Facade as any).isBigWinPresentationActive)
        const canSkipWhileNotPlaying = isBigWinPresentationActive || isFsCollectPresentationActive
        if (
            (this._currentUIState !== 'playing' && !canSkipWhileNotPlaying) ||
            this._isGameLocked ||
            this._isMenuShown ||
            this._isDialogShown ||
            this._isBuyBonusPopupOpen ||
            (Facade.isPopupBlockingInput && !isFsCollectPresentationActive && !isBigWinPresentationActive) ||
            event.isPrimary === false ||
            (event.pointerType === 'mouse' && event.button !== 0)
        ) {
            return
        }

        // Quick Spin only changes reel speed. Do not skip the round or reset Auto Spin.
        if (this.isPointerOnQuickSpinButton(event)) {
            return
        }

        this._lastGlobalSkipPointerAt = Date.now()

        this.requestSpinStopSound()

        // Only the panel's Spin / Stop buttons end an auto spin session. This handler runs on every
        // window pointerdown, so ending it here killed the session on any tap on the screen.
        if (!isFsCollectPresentationActive) {
            ;(Facade as any).pendingImmediateStopRequested = true
        }
        GameSignals.stopButtonClicked.emit()
    }

    private mapPointerToStage(event: PointerEvent): PIXI.Point | null {
        const app = Facade.app
        const events = (app?.renderer as any)?.events
        if (events?.mapPositionToPoint) {
            const point = new PIXI.Point()
            events.mapPositionToPoint(point, event.clientX, event.clientY)
            return point
        }

        const canvas = ((app as any)?.canvas || (app as any)?.view) as HTMLCanvasElement | undefined
        if (!canvas || !app?.screen) {
            return null
        }

        const rect = canvas.getBoundingClientRect()
        if (!rect.width || !rect.height) {
            return null
        }

        return new PIXI.Point(
            ((event.clientX - rect.left) / rect.width) * app.screen.width,
            ((event.clientY - rect.top) / rect.height) * app.screen.height
        )
    }

    private isQuickSpinDisplayObject(target: unknown): boolean {
        let current = target as {
            parent?: unknown
            name?: string
            label?: string
            __dgQuickSpinButton?: boolean
        } | null
        while (current) {
            if (
                current.__dgQuickSpinButton ||
                current.name === 'Quick Spin Button' ||
                current.label === 'Quick Spin Button'
            ) {
                return true
            }

            current = (current.parent as typeof current) || null
        }

        return false
    }

    private isPointerOnQuickSpinButton(event: PointerEvent): boolean {
        const point = this.mapPointerToStage(event)
        const events = (Facade.app?.renderer as any)?.events
        if (point && events?.rootBoundary?.hitTest) {
            const hit = events.rootBoundary.hitTest(point.x, point.y)
            if (this.isQuickSpinDisplayObject(hit)) {
                return true
            }
        }

        const stage = Facade.app?.stage || Facade.stage
        if (!stage || !point) {
            return false
        }

        return this.findQuickSpinDisplayObjectAt(stage, point) != null
    }

    private findQuickSpinDisplayObjectAt(root: PIXI.Container, point: PIXI.Point): PIXI.Container | null {
        if (root.visible && (root as any).worldVisible !== false && this.isQuickSpinDisplayObject(root)) {
            if (typeof (root as any).containsPoint === 'function' && (root as any).containsPoint(point)) {
                return root
            }

            const bounds = root.getBounds?.()
            if (bounds && typeof bounds.contains === 'function' && bounds.contains(point.x, point.y)) {
                return root
            }
        }

        const children = root.children || []
        for (let index = 0; index < children.length; index++) {
            const hit = this.findQuickSpinDisplayObjectAt(children[index] as PIXI.Container, point)
            if (hit) {
                return hit
            }
        }

        return null
    }

    /**
     * The stop press has a sound of its own, and it belongs to spinning reels being cut short. Any
     * input does that — the panel button, the space bar, and a tap anywhere on the screen, which is
     * how the spin is stopped on mobile — so none of them is singled out here.
     *
     * Only the press that actually cuts the spin short sounds, once, which is what the single-shot
     * arming enforces: the reels can only be stopped once per spin, while the presses keep coming.
     * One press already reaches this twice (the window handler and the button's own handler), and
     * an impatient player adds several more, each of which would restart the sample on top of the
     * previous one and turn it into a thud.
     */
    private requestSpinStopSound = (): void => {
        if (!this._isSpinStopSoundArmed || !UIManager.REELS_SPINNING_STATES.has(String(Facade.currentState || ''))) {
            return
        }

        this._isSpinStopSoundArmed = false
        ;(GameSignals as any).spinStopPressed?.emit()
    }

    /**
     * True while the player is watching a win being presented (paylines, commendation, FS coin
     * collect) rather than waiting on spinning reels. Stopping auto spins during one of these must
     * leave the presentation running, so it is only the reels that a press is allowed to cut short.
     */
    private isWinPresentationActive = (): boolean => {
        if ((Facade as any).isBigWinPresentationActive || (Facade as any).isFsCollectPresentationActive) {
            return true
        }

        return UIManager.WIN_PRESENTATION_STATES.has(String(Facade.currentState || ''))
    }

    /**
     * Auto spins end on the first press. Returns true when that press has been fully consumed by
     * stopping the session and must not travel on as a skip request.
     */
    private consumePressAsAutoplayStop = (): boolean => {
        if (!Facade.isAuto) {
            return false
        }

        GameSignals.stopAutoplay.emit()

        return this.isWinPresentationActive()
    }

    private consumeRecentGlobalSkipPointer = (): boolean => {
        if (Date.now() - this._lastGlobalSkipPointerAt > UIManager.GLOBAL_SKIP_DEDUPLICATION_MS) {
            return false
        }

        this._lastGlobalSkipPointerAt = 0
        return true
    }

    private onBuyBonusPopupOpen = (): void => {
        const isBigWinPresentationActive = Boolean((Facade as any).isBigWinPresentationActive)
        // Reject mid-round / commendation opens (ghost hit while spinning, or click before Idle).
        if (
            (!this._model.isBuyBonusPending && Facade.currentState !== GameStatesEnum.IDLE_STATE) ||
            isBigWinPresentationActive
        ) {
            this._isBuyBonusPopupOpen = false
            ;(Facade as any).isBuyBonusPopupOpen = false
            GameSignals.hidePopup.emit()
            return
        }

        this._isBuyBonusPopupOpen = true
        Facade.isPopupBlockingInput = true
        ;(Facade as any).isBuyBonusPopupOpen = true
    }

    private onBuyBonusPopupClose = (): void => {
        if (!this._isBuyBonusPopupOpen && !(Facade as any).isBuyBonusPopupOpen) {
            return
        }

        this._isBuyBonusPopupOpen = false
        ;(Facade as any).isBuyBonusPopupOpen = false
        // Keep blocking only while a confirmed purchase spin is already armed.
        if (!this._model.isBuyBonusPending) {
            Facade.isPopupBlockingInput = false
        }
        ;(Facade as any).pendingImmediateStopRequested = false
    }

    private onInit = (initData: IInitResponse, reInit: boolean = false) => {
        this._operatorConfig = this.resolveOperatorRuntimeConfig(initData)
        this.applyOperatorConfigToDefaults(this._operatorConfig)

        const quickSpinAllowedByOperator =
            this._operatorConfig.isQuickSpin !== false && this._operatorConfig.noTurboSpin !== true
        const autoplayAllowedByOperator = this._operatorConfig.noAutoPlay !== true

        this._isQuickSpinActive = quickSpinAllowedByOperator && Facade.urlParams.quickSpin !== 'false'

        if (!reInit) {
            UIAPI.connectAll(this.onUIConnections)
        }

        const isAutoPlayEnabled = autoplayAllowedByOperator && Facade.urlParams.autoSpin !== 'false'

        const localStorageData = {
            music: RestoreUtil.getKeyData(RestoreUtil.MUSIC_KEY),
            sound: RestoreUtil.getKeyData(RestoreUtil.SOUND_KEY),
            volume: RestoreUtil.getKeyData(RestoreUtil.VOLUME_KEY),
            skipIntro: RestoreUtil.getKeyData(RestoreUtil.SKIP_INTRO_KEY),
            spacebarSpin: RestoreUtil.getKeyData(RestoreUtil.SPACEBAR_SPIN_KEY),
            turboSpin: RestoreUtil.getKeyData(RestoreUtil.TURBO_SPIN_KEY),
        }

        const fallbackVolume =
            typeof Facade.defaultConfig?.volume === 'number' ? Facade.defaultConfig.volume : UIManager.DEFAULT_VOLUME
        const persistedVolume = Number(localStorageData.volume)
        const fallbackMuted = localStorageData.sound === 0 && localStorageData.music === 0
        const volume = this.clampVolume(
            Number.isFinite(persistedVolume) ? persistedVolume : fallbackMuted ? 0 : fallbackVolume
        )
        const hasVolume = volume > 0

        this._isSpacebarActive = Number.isInteger(localStorageData.spacebarSpin)
            ? !!localStorageData.spacebarSpin
            : Facade.defaultConfig.spacebarSpin
        const storedTurboValue = Number.isInteger(localStorageData.turboSpin)
            ? !!localStorageData.turboSpin
            : Facade.defaultConfig.turboSpin
        Facade.isTurboSpin = quickSpinAllowedByOperator ? !!storedTurboValue : false
        Facade.isQuickSpin = Facade.isTurboSpin
        this._isQuickSpinActive = Facade.isTurboSpin

        const balance = this._model.balance
        const isBuyBonusEnabledForRuntime = this.isBuyBonusEnabledForRuntime(this._operatorConfig)
        const hideHomeForCurrentDevice = Platform.isMobile()
            ? this._operatorConfig.hideHomeButtonMobile === true
            : this._operatorConfig.hideHomeButtonInDesktop === true
        const shouldShowHistoryButton = this._operatorConfig.hideHistoryButton !== true
        const shouldShowSpacebarOption = !(this._operatorConfig.customBehaviorKeys || []).includes(
            'HIDE_SPACEBAR_SETTINGS_HINT'
        )
        const runtimeLobbyUrl = this.resolveLobbyUrl()
        const runtimeDepositUrl = this.resolveDepositUrl()

        const data: any = {
            game_version: Facade.gameVersion,
            casino_token: '',
            bonus_buy_enabled: isBuyBonusEnabledForRuntime,
            premium_spin_text_visible: false,
            bonus_buy_text_visible: isBuyBonusEnabledForRuntime,
            bonus_buy_items: this.getBonusBuyItems(),
            balance: balance,
            currency_symbol: this._model.initData.player.currency,
            home_url: hideHomeForCurrentDevice ? '' : runtimeLobbyUrl || '#',
            show_win_history: shouldShowHistoryButton,
            lobby_url: hideHomeForCurrentDevice ? '' : runtimeLobbyUrl,
            show_lobby_button: !hideHomeForCurrentDevice,
            show_home_button: !hideHomeForCurrentDevice,
            deposit_url: this._operatorConfig.hideDeposit === true ? '' : runtimeDepositUrl,
            auto_play_arr: [],
            stop_min_single_win: 0,
            stop_max_single_win: 100,
            max_bet: this._model.maxBet,
            stop_balance_low_arr: [10, 20, 30, 40],
            stop_balance_high_arr: [10, 20, 30, 40],
            bets_arr: this._model.bets,
            current_bet: +this._model.currentBet,
            current_base_bet: +this._model.currentBaseBet,
            current_auto_play: 0,
            current_stop_single_win: 0,
            current_stop_balance_low: 0,
            current_stop_balance_high: 0,
            sound: hasVolume,
            music: hasVolume,
            volume,
            spacebar_spin: this._isSpacebarActive,
            payout: Facade.payouts,
            rtp: +this._model.initData.game.rtp,
            bonus_rtp: 0,
            premium_rtp: 0,
            autoplay_enabled: isAutoPlayEnabled,
            quickspin_enabled: this._isQuickSpinActive,
            turbo_spin_enabled: quickSpinAllowedByOperator,
            display_max_win: true,
            country: '',
            max_win_text: `1x${getMaxWinBetMultiplier()}`,
            notification_server_url: '',
            notification_server_token: '',
            show_splash_toggle_enabled: true,
            show_spacebar: shouldShowSpacebarOption,
        }
        GameSignals.balanceUpdate.emit(this._model.balance)
        UIAPI.emit(UIGameEvent.init, data)

        if (!reInit) {
            this.addKeyboardListener()
        }

        const showSplash: boolean =
            localStorageData.skipIntro !== null ? !localStorageData.skipIntro : !Facade.defaultConfig.skipIntro

        UIAPI.emit(UIGameEvent.show_splash, { value: showSplash })
        UIAPI.emit(UIGameEvent.turbo_spin, { value: Facade.isTurboSpin })
        UIAPI.emit(UIGameEvent.volume, { value: volume })
        GameSignals.setMasterVolume.emit(volume)

        GameSignals.betHasChanged.emit(this._model.currentBet, this._model.currentBaseBet)

        this.setGameState('ready')
    }

    private isLocalOrStagingRuntime = (): boolean => {
        if (typeof window === 'undefined') return false
        const host = (window.location.hostname || '').toLowerCase()
        if (host === 'localhost' || host === '127.0.0.1' || host.includes('staging')) {
            return true
        }
        // Local network device testing (e.g. phone → Mac IP)
        return (
            /^192\.168\.\d+\.\d+$/.test(host) ||
            /^10\.\d+\.\d+\.\d+$/.test(host) ||
            /^172\.(1[6-9]|2\d|3[0-1])\.\d+\.\d+$/.test(host)
        )
    }

    private parseBoolean(value: unknown): boolean {
        if (typeof value === 'boolean') return value
        if (typeof value === 'number') return value === 1
        const normalized = String(value ?? '')
            .trim()
            .toLowerCase()
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on'
    }

    private resolveOperatorRuntimeConfig(initData: IInitResponse): OperatorFeatureConfig {
        const params: any = Facade.urlParams || {}
        const siteCode = String(params.siteCode || '').trim()
        const networkCode = String(params.networkCode || '').trim() || getNetworkCodeFromSite(siteCode)

        const resolved = resolveOperatorConfig({
            siteCode,
            networkCode,
            runtime: {
                amountType: initData?.previous_round?.amount_type,
                lobbyUrl: this.resolveLobbyUrl(),
                depositUrl: this.resolveDepositUrl(),
                gameName: params.gameCode || params.game_code,
                gameOnlyPortraitMode: Facade.orientation === Orientation.PORTRAIT,
                dir: params.dir,
                hidePromoBtn: this.parseBoolean(params.hide_promo_btn || params.hidePromoBtn),
                isPmgGames: String(params.platform || '')
                    .toLowerCase()
                    .includes('pmg'),
            },
        })

        const effective = isBuyBonusDisabledByServer(initData) ? { ...resolved, hideBuyBonus: true } : resolved

        ;(Facade as any).operatorFeatureConfig = effective
        return effective
    }

    private applyOperatorConfigToDefaults(config: OperatorFeatureConfig): void {
        if (!Facade.defaultConfig) {
            return
        }

        if (config.isQuickSpin === false || config.noTurboSpin === true) {
            Facade.defaultConfig.turboSpin = false
        }

        // Operator `noSwipeUp` disables swipe — but keep DefaultConfig when testing on local/LAN,
        // otherwise DefaultConfig.mobileSwipeSpin.enabled never takes effect on device.
        if (config.noSwipeUp === true && !this.isLocalOrStagingRuntime()) {
            const existing = Facade.defaultConfig.mobileSwipeSpin
            if (existing && typeof existing === 'object') {
                Facade.defaultConfig.mobileSwipeSpin = { ...existing, enabled: false }
            } else {
                Facade.defaultConfig.mobileSwipeSpin = { enabled: false }
            }
        }

        if (config.hideBuyBonus === true) {
            ;(Facade.defaultConfig as any).hideBuyBonus = true
        }
    }

    private resolveLobbyUrl(): string {
        const params: any = Facade.urlParams || {}
        return params.lobby_url || params.home_url || params.lobbyUrl || params.homeUrl || ''
    }

    private resolveDepositUrl(): string {
        const params: any = Facade.urlParams || {}
        return params.deposit_url || params.depositUrl || ''
    }

    private isBuyBonusEnabledForRuntime = (operatorConfig?: OperatorFeatureConfig): boolean => {
        if (operatorConfig?.hideBuyBonus === true) {
            return false
        }

        return this.isLocalOrStagingRuntime()
    }

    private getBonusBuyItems = (): any[] => {
        return []
    }

    private togglePremiumSpinsMode = (state: boolean) => {
        this._model.isPremiumSpinsActive = state
        this.onInit(null, true)
        UIAPI.emit(UIGameEvent.bonus_mode, { value: state })
    }

    private onLoadingProgress = (progress: number) => {
        const raw = Number.isFinite(progress) ? Math.max(0, Math.min(100, Number(progress))) : 0

        const monotonic = Math.max(this._lastLoadingProgress, raw)

        this._lastLoadingProgress = monotonic

        UIAPI.emit('loading', { value: monotonic })
    }

    private onGameIsReady = () => {
        this._currentUIState = 'ready'
        this._lastLoadingProgress = 100
        UIAPI.emit('loading', { value: 100 })
        UIAPI.emit(UIGameEvent.game_ready, {})
    }

    private stopAutoPlay = () => {
        UIAPI.emit(UIGameEvent.autoplay, { value: 0 })
    }

    private updateAutoplayValue = (value: number) => {
        UIAPI.emit(UIGameEvent.autoplay, { value })
    }

    private onCompleteSplashScreenState = () => {
        gsap.to(this._infoTextObject, {
            duration: 1,
            onComplete: () => {
                const text = this.getLegacyLikeIdlePrompt()
                this.updateInfoText(text)
            },
        })
    }

    private onIdleState = () => {
        if (this._model.freeRoundsActive()) {
            return
        }

        gsap.to(this._infoTextObject, {
            duration: 30,
            onComplete: () => {
                const text = this.getLegacyLikeIdlePrompt()
                this.updateInfoText(text)
                this._infoTextObject.textCounter = 0
                this._infoTextObject.spinCounter = 0
                this._infoTextObject.shownIndexes = []
            },
        })
    }

    private updateInfoTextRandomly = () => {
        gsap.killTweensOf(this._infoTextObject)
        let texts: string[] = []
        ;['uiText2', 'uiText3', 'uiText4', 'uiText5'].forEach((key: string) => {
            texts.push(TranslationUtils.translate(key))
        })

        if (!this._infoTextObject.shownIndexes.length && !this._infoTextObject.spinCounter) {
            this._infoTextObject.spinCounter++
            const shownIndex: number = 0
            this._infoTextObject.shownIndexes.push(shownIndex)
            this.updateInfoText(texts[shownIndex])
            return
        }

        if (this._infoTextObject.spinCounter < 2) {
            this._infoTextObject.spinCounter++

            return
        }

        this._infoTextObject.spinCounter = 0

        if (this._infoTextObject.shownIndexes.length === 4) {
            this._infoTextObject.shownIndexes = []
            this.updateInfoTextRandomly()
            return
        }
        let randomIndex: number = Math.floor(Math.random() * texts.length)

        while (this._infoTextObject.shownIndexes.includes(randomIndex)) {
            randomIndex = Math.floor(Math.random() * texts.length)
        }

        this._infoTextObject.spinCounter++
        this._infoTextObject.shownIndexes.push(randomIndex)
        this.updateInfoText(texts[randomIndex])
    }

    private displayUI = (value: boolean) => {
        UIAPI.emit(UIGameEvent.show_ui, { value: value })
    }
    //UPDATE
    private onBetHasChanged = (value: number, baseValue: number) => {
        UIAPI.emit(UIGameEvent.bet, { value: value, base_value: baseValue })
    }

    private onBetPlusClicked = () => {
        this.stepBet(1)
    }

    private onBetMinusClicked = () => {
        this.stepBet(-1)
    }

    private stepBet(step: 1 | -1) {
        // Stake is frozen for the whole autoplay session, not only while the reels spin.
        if (Facade.isAuto) {
            return
        }

        const maxBet = Number(this._model.maxBet || 0)
        const availableBets = [...new Set(this._model.bets)]
            .filter((bet) => Number.isFinite(bet) && bet > 0)
            .filter((bet) => (maxBet > 0 ? bet <= maxBet : true))
            .sort((a, b) => a - b)

        if (!availableBets.length) {
            return
        }

        const currentBet = Number(this._model.currentBet)
        const exactIndex = availableBets.indexOf(currentBet)

        let targetBet = currentBet

        if (exactIndex >= 0) {
            const nextIndex = Math.min(Math.max(exactIndex + step, 0), availableBets.length - 1)
            targetBet = availableBets[nextIndex]
        } else if (step > 0) {
            targetBet = availableBets.find((bet) => bet > currentBet) ?? availableBets[availableBets.length - 1]
        } else {
            const lowerOrEqual = availableBets.filter((bet) => bet < currentBet)
            targetBet = lowerOrEqual.length ? lowerOrEqual[lowerOrEqual.length - 1] : availableBets[0]
        }

        if (targetBet !== currentBet) {
            GameSignals.changeBet.emit(targetBet)
        }
    }

    private onUIConnections = (data: any) => {
        const actionsAllowedWhenGameLocked = [
            UIEvent.set_volume,
            UIEvent.set_sound,
            UIEvent.set_music,
            UIEvent.set_spacebar_spin,
            UIEvent.set_turbo_spin,
            UIEvent.set_show_splash,
            UIEvent.menu_state,
            UIEvent.dialogs_state,
            // Settings can close / sync autoplay stop prefs while the panel is locked.
            UIEvent.set_stop_single_win,
            UIEvent.set_stop_balance_lower,
            UIEvent.set_stop_balance_higher,
            UIEvent.set_autoplay_stop_cash_decrease_enabled,
            UIEvent.set_autoplay_stop_cash_increase_enabled,
            UIEvent.set_autoplay_stop_cash_decrease,
            UIEvent.set_autoplay_stop_cash_increase,
            'set_autoplay_stop_any_win',
        ]

        if (this._isGameLocked && !actionsAllowedWhenGameLocked.includes(data.action)) return

        switch (data.action) {
            case UIEvent.set_bet:
                if (Facade.isAuto) {
                    break
                }
                GameSignals.changeBet.emit(data.data.value)
                break

            case UIEvent.set_autoplay:
                if (this._operatorConfig.noAutoPlay === true) {
                    break
                }
                if (data.data.value > 0) {
                    GameSignals.startAutoplay.emit(data.data.value)
                } else {
                    GameSignals.stopAutoplay.emit()
                }
                break

            case UIEvent.set_stop_single_win:
                GameSignals.setAutoplaySettings.emit({ singleWin: data.data.value })
                break

            case 'set_autoplay_stop_any_win':
                GameSignals.setAutoplaySettings.emit({ stopOnAnyWin: !!data.data.value })
                break

            case UIEvent.set_stop_balance_lower:
                GameSignals.setAutoplaySettings.emit({ balanceLower: data.data.value })
                break

            case UIEvent.set_stop_balance_higher:
                GameSignals.setAutoplaySettings.emit({ balanceHigher: data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_decrease_enabled:
                GameSignals.setAutoplaySettings.emit({ balanceLowerEnabled: !!data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_increase_enabled:
                GameSignals.setAutoplaySettings.emit({ balanceHigherEnabled: !!data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_decrease:
                GameSignals.setAutoplaySettings.emit({ balanceLower: data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_increase:
                GameSignals.setAutoplaySettings.emit({ balanceHigher: data.data.value })
                break

            case UIEvent.set_spacebar_spin:
                this._isSpacebarActive = data.data.value
                UIAPI.emit(UIGameEvent.spacebar_spin, { value: this._isSpacebarActive })
                RestoreUtil.setKeyData(RestoreUtil.SPACEBAR_SPIN_KEY, `${+data.data.value}`)
                break
            case UIEvent.set_show_splash:
                GameSignals.skipIntroToggled.emit(!data.data.value)
                break
            case UIEvent.set_sound:
                UIAPI.emit(UIGameEvent.sound, { value: data.data.value })
                GameSignals.toggleSoundGroup.emit('sounds', data.data.value)
                RestoreUtil.setKeyData(RestoreUtil.SOUND_KEY, `${+data.data.value}`)
                this.syncVolumeWithLegacyToggles(data.data.value, Facade.defaultConfig?.music ?? true)
                break

            case UIEvent.set_music:
                UIAPI.emit(UIGameEvent.music, { value: data.data.value })
                GameSignals.toggleSoundGroup.emit('music', data.data.value)
                RestoreUtil.setKeyData(RestoreUtil.MUSIC_KEY, `${+data.data.value}`)
                this.syncVolumeWithLegacyToggles(Facade.defaultConfig?.sound ?? true, data.data.value)
                break

            case UIEvent.set_volume: {
                const volume = this.clampVolume(Number(data.data.value))
                const isAudioEnabled = volume > 0

                UIAPI.emit(UIGameEvent.volume, { value: volume })
                UIAPI.emit(UIGameEvent.sound, { value: isAudioEnabled })
                UIAPI.emit(UIGameEvent.music, { value: isAudioEnabled })

                // The slider owns both toggles, so it states that intent explicitly. Setting the
                // master volume no longer implies it, otherwise a group switched off in settings
                // could not stay off.
                GameSignals.toggleSoundGroup.emit('sounds', isAudioEnabled)
                GameSignals.toggleSoundGroup.emit('music', isAudioEnabled)
                GameSignals.setMasterVolume.emit(volume)

                RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, `${volume}`)
                RestoreUtil.setKeyData(RestoreUtil.SOUND_KEY, `${+isAudioEnabled}`)
                RestoreUtil.setKeyData(RestoreUtil.MUSIC_KEY, `${+isAudioEnabled}`)
                break
            }

            case UIEvent.clicked_play:
            case UIEvent.hold_play_end: {
                // The capture-phase global skip handler already stopped the reels for this very
                // gesture and swallows the button event. Autoplay still has to end here, otherwise
                // the player's first press only shortens the current spin and the session rolls on.
                const playHandledByGlobalSkip = this.consumeRecentGlobalSkipPointer()
                if (
                    Facade.requestOngoing ||
                    this._isBuyBonusPopupOpen ||
                    (Facade.isPopupBlockingInput &&
                        !(Facade as any).isFsCollectPresentationActive &&
                        !(Facade as any).isBigWinPresentationActive)
                )
                    return
                if (this.consumePressAsAutoplayStop()) return
                if (playHandledByGlobalSkip) return
                GameSignals.spinButtonClicked.emit()
                break
            }
            case UIEvent.clicked_home:
            case UIEvent.clicked_lobby:
                Utils.goToLobby()
                break

            case UIEvent.clicked_win_history:
                window.open('', '_blank')

                break

            case UIEvent.clicked_stop: {
                const stopHandledByGlobalSkip = this.consumeRecentGlobalSkipPointer()
                if (
                    Facade.isPopupBlockingInput &&
                    !(Facade as any).isFsCollectPresentationActive &&
                    !(Facade as any).isBigWinPresentationActive
                ) {
                    return
                }
                this.requestSpinStopSound()
                if (this.consumePressAsAutoplayStop()) return
                if (stopHandledByGlobalSkip) return
                ;(Facade as any).pendingImmediateStopRequested = true
                GameSignals.stopButtonClicked.emit()
                break
            }

            case UIEvent.menu_state:
                this._isMenuShown = data.data.value
                if (data.data.value) {
                    ;(GameSignals as any).settingsMenuOpened?.emit()
                }
                break

            case UIEvent.dialogs_state:
                this._isDialogShown = data.data.value
                GameSignals.changeDialogState.emit(this._isDialogShown)
                break

            case UIEvent.clicked_button:
                GameSignals.buttonClicked.emit(data.data.name)
                break

            case UIEvent.bonus_buy_clicked:
                if (this._currentUIState === 'ready') {
                    if (data.data.id == 2) {
                        //PREMIUM SPINS
                        GameSignals.premiumSpinsToggled.emit(!this._model.isPremiumSpinsActive)
                    } else if (data.data.id == 1) {
                        //BONUS BUY
                        if (!this.isBuyBonusEnabledForRuntime(this._operatorConfig) || !canAffordBuyBonus()) {
                            break
                        }
                        if (this._model.isPremiumSpinsActive) {
                            GameSignals.premiumSpinsToggled.emit(false)
                        }
                        ;(GameSignals as any).buyBonusOpenButtonClicked?.emit()
                        GameSignals.buyBonusClicked.emit(data.data.id)
                    }
                    UIAPI.emit(UIGameEvent.close_menu, {})
                    GameSignals.updateUIWinField.emit('')
                }
                break

            case UIEvent.set_turbo_spin:
                if (this._operatorConfig.isQuickSpin === false || this._operatorConfig.noTurboSpin === true) {
                    break
                }
                this.toggleTurboSpin(data.data.value)
                break

            case UIEvent.set_bonus_mode:
                GameSignals.premiumSpinsToggled.emit(data.data.value)
                break
        }
    }

    private toggleTurboSpin = (state: boolean) => {
        Facade.isTurboSpin = state
        Facade.isQuickSpin = state
        this._isQuickSpinActive = state

        UIAPI.emit(UIGameEvent.turbo_spin, { value: state })
        GameSignals.turboToggled.emit(state)
        RestoreUtil.setKeyData(RestoreUtil.TURBO_SPIN_KEY, `${+state}`)
    }

    private onLockGamePanel = () => {
        this.setGameState('playing')
    }

    private onUnlockGamePanel = () => {
        if (this._model.isBuyBonusPending && Facade.currentState !== GameStatesEnum.IDLE_STATE) {
            this.setGameState('playing')
            return
        }

        this.setGameState('ready')
    }

    private onStartSpin = (bet: number) => {
        this._isSpinStopSoundArmed = true

        if (this._model.isBuyBonusPending) {
            this._isBuyBonusPopupOpen = false
            ;(Facade as any).isBuyBonusPopupOpen = false
            Facade.isPopupBlockingInput = false
            ;(Facade as any).pendingImmediateStopRequested = false
        }

        this.updateFreeSpinsInfo()
        // Keep accumulated FS / PFS wins across chained START_SPINs (no StopRound mid-feature).
        if (!this._model.roundState.isFreeSpins && !this._model.isPromoSpinsRound()) {
            this._uiPanelRoundWinTotal = 0
        }
        this.clearWinningComboTickerDelay()

        this.setGameState('playing')

        // Framework panel clears Win Value on startSpin unless FS/H&W. Re-assert PFS
        // total after those handlers so the meter never blanks mid-session.
        if (this._model.isPromoSpinsRound() && this._uiPanelRoundWinTotal > 0) {
            const keptWin = this._uiPanelRoundWinTotal
            queueMicrotask(() => {
                if (!this._model.isPromoSpinsRound() || this._uiPanelRoundWinTotal !== keptWin) {
                    return
                }

                ;(GameSignals as any).setWinPanelValue?.emit(keptWin)
            })
        }

        // FS / H&W / Promo Free Spins are free — do not provisionally debit the wallet.
        // HoldAndWinIntro emits startSpin after the trigger spin already deducted the stake.
        if (
            this._model.roundState.isFreeSpins ||
            this._model.spinState.isHoldAndWinActive ||
            this._model.isPromoSpinsRound() ||
            this._model.freeRoundsActive()
        ) {
            return
        }

        GameSignals.updateUIWinField.emit('')

        // For buy bonus, `bet` is already the purchase cost (StartSpinState).
        const provisionalDeduction = bet / 100
        if (this._model.balance - provisionalDeduction < 0) {
            return
        }

        const provisionalBalance = this._model.balance - provisionalDeduction

        UIAPI.emit(UIGameEvent.balance, { value: provisionalBalance })
        GameSignals.balanceHasChanged.emit(provisionalBalance)
        this.updateInfoText(UIManager.LEGACY_SPIN_START_TEXT)
    }

    private onShowRegularWin = (data: ShowRegularWinningsData) => {
        if (!data?.win || data.win <= 0) {
            return
        }

        this.clearWinningComboTickerDelay()
        this._winningComboTickerDelay = gsap.delayedCall(1, () => {
            const formattedWin = toMoney(toMajorUnits(data.win), 2)
            this.updateInfoText(`${UIManager.LEGACY_WINNING_COMBO_PREFIX} ${formattedWin}`)
        })
    }

    private onUpdateWinPanelValueForUi = (value: number, duration: number = -1, force: boolean = false) => {
        if (value < 0) {
            return
        }

        if (value === 0 && !force) {
            return
        }

        this._uiPanelRoundWinTotal += value
        UIAPI.emit(UIGameEvent.update_win_info, { text: toMoney(this._uiPanelRoundWinTotal, 2) })
        this.syncBalanceWithWinPanelDuringFeature()
    }

    private onSetWinPanelValueForUi = (value: number) => {
        const absolute = Number(value)
        if (!Number.isFinite(absolute) || absolute < 0) {
            return
        }

        this._uiPanelRoundWinTotal = absolute
        UIAPI.emit(UIGameEvent.update_win_info, {
            text: absolute > 0 ? toMoney(this._uiPanelRoundWinTotal, 2) : '',
        })
        this.syncBalanceWithWinPanelDuringFeature()
    }

    private onSetWinPanelValueAtLeastForUi = (value: number) => {
        const absolute = Number(value)
        if (!Number.isFinite(absolute) || absolute < 0) {
            return
        }

        if (absolute <= this._uiPanelRoundWinTotal) {
            return
        }

        this._uiPanelRoundWinTotal = absolute
        UIAPI.emit(UIGameEvent.update_win_info, {
            text: toMoney(this._uiPanelRoundWinTotal, 2),
        })
        this.syncBalanceWithWinPanelDuringFeature()
    }

    /**
     * FS / H&W: credit Balance in the same turn as the Win field.
     * Microtask runs after every updateWinPanelValue / setWinPanelValue listener
     * (including the Pixi panel Win Value) so both meters paint together.
     */
    private syncBalanceWithWinPanelDuringFeature = () => {
        if (
            !this._model.roundState.isFreeSpins &&
            !this._model.spinState?.isHoldAndWinActive &&
            !this._model.isPromoSpinsRound()
        ) {
            return
        }

        queueMicrotask(() => {
            GameSignals.balanceHasChanged.emit(this.resolveFeatureDisplayBalance())
        })
    }

    /**
     * Server balance minus the FS win the Win field has not presented yet. On the final free spin
     * that difference is the jackpot-ladder prize, which FreeSpinsSummaryState credits when the
     * summary popup opens — until then the wallet must only show the payline wins.
     */
    private resolveFeatureDisplayBalance(): number {
        const withheldWin = getUncreditedFreeSpinsWinMajorUnits(this._model, this._uiPanelRoundWinTotal)

        if (!(withheldWin > 0)) {
            return this._model.balance
        }

        return Math.max(0, Number((this._model.balance - withheldWin).toFixed(2)))
    }

    private onBonusStart = () => {
        this.stopAutoPlay()
    }

    private onBonusStopComplete = () => {
        // Spin must be usable immediately after FS → base. The old 1.5s delay left UI in
        // 'playing', so pointer-down skip-dedupe swallowed several Spin clicks.
        this._isGameLocked = false
        this.setGameState('ready')
    }

    private onStopSpin = () => {
        const isPromoSpins = this._model.isPromoSpinsRound()

        if (
            Facade.isAuto ||
            this._model.freeRoundsActive() ||
            this._model.roundState.isFreeSpins ||
            (this._model.spinData.freebet &&
                this._model.spinData.freebet.amount_left === this._model.spinData.freebet.total_amount_given) ||
            this._model.isFreeSpinsRestore ||
            isPromoSpins
        ) {
            this.setGameState('ready', {
                bonus_buy: 'disabled',
                autoplay: 'disabled',
                autoplay_stop: 'disabled',
                bet: 'disabled',
            })

            return
        }

        if (!this._model.spinData.freeround) {
            this.setGameState('ready')
        }
    }

    private onRoundStop = () => {
        if (!this._model.freeRoundsActive() && this._model.spinData?.freeround) {
            GameSignals.stopAutoplay.emit()
            GameSignals.freeRoundsCompleted.emit()

            if (Utilities.isLocalStorageAvailable()) {
                localStorage.removeItem(`${Facade.urlParams.playerUid}_freeround`)
            }

            this._infoTextObject.textCounter = 0
            this._infoTextObject.spinCounter = 0
            this._infoTextObject.shownIndexes = []
            this.updateInfoTextRandomly()
        }

        if (this._model.freeRoundsActive() || this._model.roundState.isFreeSpins) {
            this.setGameState(this._currentUIState, {
                bet: 'disabled',
                bonus_buy: 'disabled',
            })
        }
    }

    private onBonusStartComplete = () => {
        if (this._model.roundState.isFreeSpins) {
            // Clear trigger-spin wins so FS meter starts fresh.
            this._uiPanelRoundWinTotal = 0
            const totalSpinsCount = this._model.spinData.freebet.total_amount_given
            const playedSpins = Math.min(totalSpinsCount - this._model.spinData.freebet.amount_left, totalSpinsCount)
            const counterText = TranslationUtils.translate('freespinCounter', [playedSpins, totalSpinsCount])
            const size: number = Platform.isMobile() ? 15 : UIManager.UI_INFO_TEXT_SIZE
            this.updateInfoText(counterText, size)
            gsap.killTweensOf(this._infoTextObject)
        }
    }

    private updateFreeSpinsInfo = (restored: boolean = false) => {
        if (this._model.roundState.isFreeSpins) {
            const totalSpinsCount = this._model.spinData.freebet.total_amount_given
            const playedSpins = Math.min(
                totalSpinsCount - this._model.spinData.freebet.amount_left + (restored ? 0 : 1),
                totalSpinsCount
            )
            const counterText = TranslationUtils.translate('freespinCounter', [playedSpins, totalSpinsCount])
            const size: number = Platform.isMobile() ? 15 : UIManager.UI_INFO_TEXT_SIZE
            this.updateInfoText(counterText, size)
            gsap.killTweensOf(this._infoTextObject)
        }
    }

    private onFreeRoundSpin = (restored: boolean = false) => {
        if (this._model.freeRoundsActive() && !this._model.roundState.isFreeSpins) {
            this.setGameState(this._currentUIState, {
                bet: 'disabled',
                bonus_buy: 'disabled',
            })
            const counterText = TranslationUtils.translate('freeRoundDisplay', [
                this._model.spinData.freeround.total_amount_given -
                    this._model.spinData.freeround.amount_left +
                    (restored ? 0 : 1),
                this._model.spinData.freeround.total_amount_given,
            ])

            const size: number = Platform.isMobile() ? 15 : UIManager.UI_INFO_TEXT_SIZE
            this.updateInfoText(counterText, size)
            gsap.killTweensOf(this._infoTextObject)
        }
    }

    private onFreeSpinsSummary = () => {
        this._infoTextObject.textCounter = 0
        this._infoTextObject.spinCounter = 0
        this._infoTextObject.shownIndexes = []
        this.updateInfoTextRandomly()
    }

    private normalizeWinInfoText = (value: string): string => {
        const normalized = String(value ?? '').trim()

        if (!normalized) {
            return ''
        }

        // Fallback for legacy/raw producers that still send minor units as plain integers.
        if (/^-?\d+$/.test(normalized)) {
            return toMoney(toMajorUnits(Number(normalized)), 2)
        }

        return normalized
    }

    private onUpdateUIWinField = (value: string) => {
        const normalizedValue = this.normalizeWinInfoText(value)
        UIAPI.emit(UIGameEvent.update_win_info, { text: normalizedValue })
        ;(GameSignals.winInfoUpdate, { text: normalizedValue })
    }

    private onBalanceUpdate = (value: number) => {
        UIAPI.emit(UIGameEvent.balance, { value: value })
    }

    private onServerError = () => {
        this._isGameLocked = true
        this._currentUIState = 'ready'
        this.setGameState('ready')

        UIAPI.emit(UIGameEvent.balance, { value: this._model.balance })
    }

    private mute = () => {
        UIAPI.emit(UIEvent.set_volume, { value: 0 })
    }

    private unMute = () => {
        UIAPI.emit(UIEvent.set_volume, { value: UIManager.UNMUTE_VOLUME })
    }

    private clampVolume = (value: number): number => {
        if (!Number.isFinite(value)) {
            return UIManager.DEFAULT_VOLUME
        }

        return Math.max(0, Math.min(1, value))
    }

    private syncVolumeWithLegacyToggles = (soundEnabled: boolean, musicEnabled: boolean) => {
        const currentVolume = Number(RestoreUtil.getKeyData(RestoreUtil.VOLUME_KEY))
        const fallbackVolume =
            typeof Facade.defaultConfig?.volume === 'number' ? Facade.defaultConfig.volume : UIManager.UNMUTE_VOLUME

        if (!soundEnabled && !musicEnabled) {
            UIAPI.emit(UIGameEvent.volume, { value: 0 })
            GameSignals.setMasterVolume.emit(0)
            RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, '0')
            return
        }

        if (!Number.isFinite(currentVolume) || currentVolume <= 0) {
            const restoredVolume = this.clampVolume(fallbackVolume > 0 ? fallbackVolume : UIManager.UNMUTE_VOLUME)
            UIAPI.emit(UIGameEvent.volume, { value: restoredVolume })
            GameSignals.setMasterVolume.emit(restoredVolume)
            RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, `${restoredVolume}`)
        }
    }

    private onTogglePaytable = () => {
        if (this._isMenuShown) {
            UIAPI.emit(UIGameEvent.close_menu)
        } else {
            UIAPI.emit(UIGameEvent.show_menu, { id: 'paytable', nav: 'info' })
        }
    }

    private onToggleGameHelp = () => {
        if (this._isMenuShown) {
            UIAPI.emit(UIGameEvent.close_menu)
        } else {
            UIAPI.emit(UIGameEvent.show_menu, { nav: 'info' })
        }
    }

    private setGameState = (state: string, mods?: any) => {
        this._currentUIState = state
        UIAPI.emit(UIGameEvent.game_state, {
            state: this._currentUIState,
            mods,
        })
    }

    private updateInfoText = (text: string, size: number = UIManager.UI_INFO_TEXT_SIZE) => {
        UIAPI.emit(UIGameEvent.update_info_text, { text, size, white_space: 'unset' })
        this._infoTextObject.text = text
    }

    private getLegacyLikeIdlePrompt = (): string => {
        const translated = TranslationUtils.translate('uiText1')
        return translated === 'uiText1' ? UIManager.LEGACY_IDLE_TEXT : translated
    }

    private clearWinningComboTickerDelay = () => {
        if (!this._winningComboTickerDelay) {
            return
        }

        this._winningComboTickerDelay.kill()
        this._winningComboTickerDelay = null
    }
}
